// "Operații cu definițiile" — the export/import menu the wireframe puts in the
// page header of **every** definition tab, not only the first
// (`wireframes/react/src/pages/AdminAlerte.tsx:3397-3413`: the ActiuniMenu is
// rendered for `alerte`, `sabloane`, `politici` and `canale`, and suppressed only
// on `declansari`).
//
// The bundle is one envelope covering all four object types
// (`/admin/alert-rules/export.yaml` and `/import`), so which tab the administrator
// happens to be on has never changed what the operation does — which is exactly
// why hiding it on three of the four tabs was arbitrary. The menu lives here
// rather than in each register so the four copies cannot drift.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudFileInput, MudModal } from '@egov-moldova/mud-react';
import { RowActionsMenu, onModalDismiss, useToast } from '@simsm/ui-shared';
import { apiClient } from '../app/api-client';

interface ImportResult {
  rulesImported: number;
  templatesImported: number;
  policiesImported: number;
  channelsImported: number;
}

// The formats the field takes; MUD writes the caption naming them in the page language.
const ALERT_IMPORT_ACCEPT = '.yaml,.yml,application/yaml,text/yaml';

export function AlertDefinitionsMenu(): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [importOpen, setImportOpen] = useState(false);

  async function downloadYaml(): Promise<void> {
    const res = await apiClient.get<string>('/admin/alert-rules/export.yaml', {
      transformResponse: [(v: string) => v],
      responseType: 'text',
    });
    const blob = new Blob([res.data], { type: 'application/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alerts-${new Date().toISOString().slice(0, 10)}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport(file: File | undefined): Promise<void> {
    if (!file) return;
    setImportOpen(false);
    try {
      const yaml = await file.text();
      const res = await apiClient.post<ImportResult>('/admin/alert-rules/import', { yaml });
      toast({
        variant: 'success',
        title: t('alerts.import.title'),
        text: t('alerts.import.success', {
          rules: res.data.rulesImported,
          templates: res.data.templatesImported,
          policies: res.data.policiesImported,
          channels: res.data.channelsImported,
        }),
        closeLabel: t('common.close'),
      });
      // One bundle, four registers: every list the import could have changed is
      // refetched, whichever tab it was started from.
      for (const key of [
        'alert-rules',
        'notification-templates',
        'notification-policies',
        'notification-channels',
      ]) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    } catch {
      toast({
        variant: 'error',
        title: t('alerts.import.title'),
        text: t('alerts.import.error'),
        closeLabel: t('common.close'),
      });
    }
  }

  return (
    <>
      <RowActionsMenu
        // Not a table row: this kebab sits in the page header's actions, at the
        // right of the frame, so the menu drops leftwards from it.
        align="end"
        label={t('alerts.action.definitions_menu')}
        actions={[
          {
            label: t('alerts.action.export_yaml'),
            onSelect: () => {
              void downloadYaml();
            },
          },
          { label: t('alerts.action.import_yaml'), onSelect: () => setImportOpen(true) },
        ]}
      />
      <MudModal
        closeLabel={t('common.close')}
        open={importOpen}
        size="sm"
        titleText={t('alerts.action.import_yaml')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setImportOpen(false))}
      >
        <MudFileInput
          size="md"
          accept={ALERT_IMPORT_ACCEPT}
          label={t('alerts.import.file_label')}
          helperText={t('alerts.import.file_hint')}
          onMudChange={(e: CustomEvent<{ files: File[] }>) => {
            void handleImport(e.detail.files[0]);
          }}
        />
        <div slot="actions" className="simsm-alerts__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setImportOpen(false)}
          >
            {t('alerts.confirm.action.cancel')}
          </MudButton>
        </div>
      </MudModal>
    </>
  );
}
